import { latestDueDate, summariseIndications, type InstructionImage, type QuoteWhatsApp, type QuoteWhatsAppLine } from "@probetalab/contracts";
import { money } from "./format.js";

/**
 * The preset WhatsApp answer to a price question (pilot request 2026-11-02). Modelled on how the
 * counter writes it by hand: "Buenos días, claro que sí, le salen en $860.00, hay que presentarse
 * en ayunas, traer muestra de orina, resultados mismo día". Formal "usted", es-MX. The text is
 * only a starting point: the dialog lets the counter edit it before it opens WhatsApp.
 */

const TZ = "America/Mexico_City";

/** "Buenos días" until noon, "Buenas tardes" until 7 p. m., "Buenas noches" otherwise (lab time). */
export function greetingFor(now: Date): string {
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone: TZ, hour: "numeric", hourCycle: "h23" }).format(now));
  if (hour >= 5 && hour < 12) return "Buenos días";
  if (hour >= 12 && hour < 19) return "Buenas tardes";
  return "Buenas noches";
}

/** "a", "a y b", "a, b y c". */
export function joinEs(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}

/**
 * Lower-case the first letter unless the first word is an acronym ("EGO", "VIH"). A shouted
 * multi-word text ("AYUNO DE 8 - 12 HRS", as typed in the catalogue) is lower-cased whole.
 */
function lowerFirst(s: string): string {
  if (/\s/.test(s) && s === s.toLocaleUpperCase("es-MX")) return s.toLocaleLowerCase("es-MX");
  const first = s.split(/\s/)[0] ?? "";
  if (first.length > 1 && first === first.toUpperCase() && /[A-ZÁÉÍÓÚÑ]/.test(first)) return s;
  return s.charAt(0).toLocaleLowerCase("es-MX") + s.slice(1);
}

const fold = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("es-MX").replace(/\s+/g, " ").trim();

/**
 * The union of the lines' preparation as one phrase: each line's text split into its sentences,
 * duplicates removed (accent- and case-insensitively, first wording wins), joined naturally.
 * "Ayuno de 8 horas." + "Ayuno de 8 horas. Traer muestra de orina." → "ayuno de 8 horas y traer
 * muestra de orina". Empty when no line has any.
 */
export function indicationsPhrase(lines: Pick<QuoteWhatsAppLine, "indications">[]): string {
  // Shared logic (contracts `summariseIndications`): sentences de-duplicated, "no indications"
  // entries dropped when any line has a real one.
  return joinEs(summariseIndications(lines.map((l) => l.indications)).parts.map(lowerFirst));
}

/** "No hay indicaciones previas." once, only when every line says so; otherwise null. */
export function noIndicationsSentence(lines: Pick<QuoteWhatsAppLine, "indications">[]): string | null {
  const none = summariseIndications(lines.map((l) => l.indications)).none;
  if (!none) return null;
  const lower = none.toLocaleLowerCase("es-MX");
  return `${lower.charAt(0).toLocaleUpperCase("es-MX")}${lower.slice(1)}.`;
}

const labDayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const dueFmt = new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" });

/**
 * The promised turnaround of the slowest line, or null when no line has one. Fixed turnarounds
 * read as before ("Resultados el mismo día."). When a line promises by the weekday the sample
 * arrives (ADR 0027, cultures), the answer depends on the day, so the message names the date for
 * a sample taken today: "Si la muestra se toma hoy, resultados el jueves 5 de noviembre."
 */
export function deliveryPhrase(lines: Pick<QuoteWhatsAppLine, "delivery_days" | "delivery_by_weekday">[], now: Date = new Date()): string | null {
  const days = lines.map((l) => l.delivery_days).filter((d): d is number => d !== null);
  const tables = lines.flatMap((l) => l.delivery_by_weekday ?? []);
  if (tables.length) {
    const today = labDayFmt.format(now);
    const due = latestDueDate(today, [...days.map((d) => ({ delivery_days: d })), ...tables.map((t) => ({ delivery_days: null, delivery_by_weekday: t }))])!;
    if (due === today) return "Resultados el mismo día.";
    return `Si la muestra se toma hoy, resultados el ${dueFmt.format(new Date(`${due}T12:00:00Z`)).replace(",", "")}.`;
  }
  if (!days.length) return null;
  const max = Math.max(...days);
  if (max === 0) return "Resultados el mismo día.";
  if (max === 1) return "Resultados al día siguiente.";
  return `Resultados en ${max} días.`;
}

const validityFmt = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, day: "numeric", month: "long", year: "numeric" });

/** "17 de noviembre de 2026" for a YYYY-MM-DD date. */
export function validityDate(iso: string): string {
  return validityFmt.format(new Date(`${iso}T12:00:00`));
}

/** The whole preset message. */
export function composeQuoteMessage(wa: Pick<QuoteWhatsApp, "lines" | "total_cents" | "valid_until">, now: Date = new Date()): string {
  const names = wa.lines.map((l) => l.name.trim()).filter(Boolean);
  const verb = names.length > 1 ? "le salen" : "le sale";
  const total = money(wa.total_cents);
  // One study reads as a sentence; two or more are a list, one per line, so the patient can check
  // each study at a glance (pilot request 2026-11-03: "como en lista los estudios, no todo seguido").
  const out: string[] =
    names.length > 1
      ? [`${greetingFor(now)}, claro que sí. Estos estudios ${verb} en ${total} en total:`, ...names.map((n) => `• ${n}`), ""]
      : [`${greetingFor(now)}, claro que sí. ${joinEs(names)} ${verb} en ${total}.`];
  const indications = indicationsPhrase(wa.lines);
  const none = noIndicationsSentence(wa.lines);
  if (indications) out.push(`Indicaciones: ${indications}.`);
  else if (none) out.push(none);
  const delivery = deliveryPhrase(wa.lines, now);
  if (delivery) out.push(delivery);
  if (wa.valid_until) out.push(`Cotización vigente hasta el ${validityDate(wa.valid_until)}.`);
  return out.join("\n");
}

/** The images to tick by default: active ones linked to any study of any quoted line. */
export function preselectedImageIds(images: InstructionImage[], lines: Pick<QuoteWhatsAppLine, "study_ids">[]): string[] {
  const quoted = new Set(lines.flatMap((l) => l.study_ids));
  return images.filter((img) => img.is_active && img.study_ids.some((id) => quoted.has(id))).map((img) => img.id);
}

/** The 10 digits of a Mexican phone, or null when the input does not have exactly 10 (after +52 / 52 / 521). */
export function phoneDigits(input: string): string | null {
  let d = input.replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("521")) d = d.slice(3);
  else if (d.length === 12 && d.startsWith("52")) d = d.slice(2);
  return d.length === 10 ? d : null;
}

/** A file name WhatsApp shows under the image: "indicaciones-recoleccion-de-orina.jpg". */
export function imageFilename(name: string, contentType: string): string {
  const slug = fold(name).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "imagen";
  return `indicaciones-${slug}.${contentType === "image/png" ? "png" : "jpg"}`;
}
